import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { fmt, superGroupLabel, TOOL_LINE_COLORS } from "@/lib/format";
import { GroupIcon } from "@/lib/groupIcons";
import RowDelta from "@/components/RowDelta";
import { itemValue, itemTotal, grandTotal as computeGrandTotal } from "@/lib/toolUsageDisplay";

// 값 크기에 따른 히트맵 배경(2026-07-18 피드백 — "다 같은 색이라 차별점이 안 보인다").
// 기간 내 최대값 대비 비율로 옅은 파랑의 진하기를 조절 — 사용량이 몰리는 요일·도구가
// 표만 훑어도 드러난다. 0이면 투명(빈칸 소음 방지), 최대여도 은은한 수준(알파 0.18)까지만.
function heatStyle(v, max) {
  if (!max || !v) return undefined;
  const alpha = 0.05 + 0.13 * (v / max);
  return { backgroundColor: `rgba(59,130,246,${alpha.toFixed(3)})` };
}

// 도구 그룹/엔진 단위 표 — items 는 ToolUsageSection 이 buildDisplayItems() 로 미리 만든
// 표시 목록. 접힌 슈퍼그룹은 컬럼 1개(rowSpan=2, 그 안 엔진들의 합계), 펼친 슈퍼그룹은
// 엔진마다 컬럼을 만들어 2단 헤더(그룹→엔진)로 묶는다.
// cumulative=true 면 series 값이 이미 "그 시점까지의 누적치"라 하단 합계(구간을 또 더하는 것)는
// 의미가 없어서 생략한다 — 대신 맨 위 행(최신 구간)이 곧 최종 누적치 역할을 한다.
// unit="sessions" 일 땐 행/열 합계를 단순 합산이 아니라 세션 id 합집합(union) 크기로 계산한다
// (같은 대화가 여러 도구/기간에 걸쳐 있으면 단순 합산은 중복 집계가 되기 때문).
export default function EngineUsageTrendTable({ series, items, cumulative = false, unit = "messages", onToggleExpand }) {
  const [dir, setDir] = useState("desc"); // "desc"(최신순, 기본) | "asc"(과거순)
  // 열 하이라이트(2026-07-18 피드백) — 엔진 열 헤더를 클릭하면 그 열 전체가 강조돼
  // 세로로 훑기 쉬워진다. 같은 헤더를 다시 클릭하면 해제.
  const [hiCol, setHiCol] = useState(null);
  const rows = series || [];
  if (!items || items.length === 0) {
    return <div className="rounded-lg border border-ink-200 bg-white p-6 text-center text-sm text-ink-400">표시할 도구/엔진을 선택해 주세요</div>;
  }
  if (rows.length === 0) {
    return <div className="rounded-lg border border-ink-200 bg-white p-6 text-center text-sm text-ink-400">데이터 없음</div>;
  }
  const displayRows = dir === "desc" ? rows : [...rows].reverse();

  // 인접한 같은 슈퍼그룹 항목을 묶어 colSpan 헤더를 만든다(items 는 이미 그룹 단위로 정렬돼 있음).
  const headerGroups = [];
  for (const item of items) {
    const last = headerGroups[headerGroups.length - 1];
    if (last && last.superGroup === item.superGroup) last.items.push(item);
    else headerGroups.push({ superGroup: item.superGroup, items: [item] });
  }

  const totalsById = {};
  items.forEach((item) => { totalsById[item.id] = itemTotal(rows, item, unit); });
  const grand = computeGrandTotal(rows, items, unit);
  // 히트맵 기준 최대값 — 데이터 셀·합계 행·합계 열이 각자 자기 스케일을 쓴다(2026-07-18).
  // 합계는 데이터 셀보다 한 자릿수 크므로 같은 스케일이면 데이터 셀이 전부 하얗게 죽는다.
  const maxVal = Math.max(0, ...rows.flatMap((r) => items.map((it) => itemValue(r, it, unit))));
  const totalsMax = Math.max(0, ...items.map((it) => totalsById[it.id]));
  const rowTotals = new Map(rows.map((r) => [
    r.bucket,
    unit === "sessions" ? computeGrandTotal([r], items, unit) : items.reduce((s, item) => s + itemValue(r, item, unit), 0),
  ]));
  const rowTotalMax = Math.max(0, ...rowTotals.values());
  // 직전 구간 대비 증감(2026-07-18 피드백 — "전주 대비 몇 건, 몇 % 증가") — rows 는 최신순이라
  // 다음 인덱스가 직전 구간. 필터·일별/주별/월별 재집계를 거친 값이라 화면 조건을 그대로 따른다.
  // 누적 모드에선 증감 = 그 구간 발생량이라 중복 정보 — 표시하지 않는다.
  const prevTotalByBucket = new Map();
  rows.forEach((r, i) => {
    if (i + 1 < rows.length) prevTotalByBucket.set(r.bucket, rowTotals.get(rows[i + 1].bucket) || 0);
  });
  const hiClass = (id) => (hiCol === id ? "bg-brand-100/70" : "");

  const isGroupStart = (i) => i === 0 || items[i - 1].superGroup !== items[i].superGroup;

  return (
    <div className="overflow-x-auto rounded-lg border border-ink-200">
      {/* table-fixed + 데이터 열 균등 분할(2026-07-18 피드백 — "셀 간격이 다르다") —
          예전엔 열 너비가 내용 길이(맞춤지시·기사진단 같은 긴 그룹명)에 끌려다녀서
          들쭉날쭉했다. 이제 긴 이름은 두 줄로 접히고 모든 데이터 열이 같은 폭.
          열이 많아지면(그룹 펼침) minWidth 로 가로 스크롤을 허용해 너무 좁아지지 않게. */}
      <table
        className="w-full table-fixed divide-y divide-ink-200 text-sm"
        style={{ minWidth: `${6 + items.length * 5.5 + 5}rem` }}
      >
        <thead className="bg-ink-50">
          <tr>
            <DateTh dir={dir} onToggle={() => setDir((d) => (d === "desc" ? "asc" : "desc"))} />
            {/* 그룹 헤더 클릭 = 엔진 펼침/접기(2026-07-18 피드백 — 필터까지 안 가도 표에서
                바로). 버튼에 호버 배경 + 셰브론 마이크로 애니메이션으로 "눌린다"를 예고하고,
                헤더는 가운데 정렬로 정돈(아이콘·텍스트·셰브론이 제각각이던 밸런스 수정). */}
            {headerGroups.map((g) => (
              g.items.length === 1 && g.items[0].kind === "group" ? (
                // 호버 배경은 버튼이 아니라 th 전체에 — 버튼에 주면 좁은 열에서 옆 칸을
                // 침범해 보인다(2026-07-18 피드백). 셀 전체가 클릭 영역이라 누르기도 편함.
                <th
                  key={g.superGroup}
                  rowSpan={2}
                  onClick={() => onToggleExpand?.(g.superGroup)}
                  title="클릭하면 엔진별로 펼칩니다"
                  className={`group cursor-pointer border-l border-ink-200 px-1.5 py-2 text-center align-middle text-xs font-semibold text-ink-700 transition-colors hover:bg-brand-50 hover:text-brand-700 ${hiClass(g.items[0].id)}`}
                >
                  <span className="inline-flex items-center gap-1">
                    <GroupLabel superGroup={g.superGroup}>{g.items[0].label}</GroupLabel>
                    <ChevronDown className="h-3 w-3 shrink-0 text-ink-300 transition-transform duration-200 group-hover:translate-y-0.5 group-hover:text-brand-500" />
                  </span>
                </th>
              ) : (
                <th
                  key={g.superGroup}
                  colSpan={g.items.length}
                  onClick={() => onToggleExpand?.(g.superGroup)}
                  title="클릭하면 그룹 합계로 접습니다"
                  className="group cursor-pointer border-l border-ink-200 px-1.5 py-1.5 text-center text-xs font-semibold text-ink-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
                >
                  <span className="inline-flex items-center gap-1">
                    <GroupLabel superGroup={g.superGroup}>{superGroupLabel(g.superGroup)}</GroupLabel>
                    <ChevronUp className="h-3 w-3 shrink-0 text-ink-300 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:text-brand-500" />
                  </span>
                </th>
              )
            ))}
            <th rowSpan={2} className="w-20 border-l border-ink-200 px-3 py-2 text-right align-bottom text-xs font-semibold uppercase tracking-wide text-ink-500">합계</th>
          </tr>
          <tr>
            {/* 엔진 열 헤더 클릭 = 그 열 하이라이트 토글 — 세로 스캔용. */}
            {items.map((item, i) => (
              item.kind === "group" ? null : (
                <th
                  key={item.id}
                  onClick={() => setHiCol((c) => (c === item.id ? null : item.id))}
                  title="클릭하면 이 열을 강조합니다"
                  className={`cursor-pointer px-3 py-2 text-right text-xs font-medium ${
                    hiCol === item.id ? "bg-brand-100/70 text-brand-700" : "text-ink-500 hover:text-ink-800"
                  } ${isGroupStart(i) ? "border-l border-ink-200" : ""}`}
                >
                  {item.label}
                </th>
              )
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100 bg-white">
          {/* 합계는 표 맨 위 — 스크롤 끝까지 안 내려가도 총량부터 보이게(2026-07-18 피드백).
              누적 모드에선 구간을 또 더하는 합계가 무의미해서 생략(맨 위 최신 행이 곧 누적치). */}
          {!cumulative && (
            <tr className="border-b-2 border-ink-200 bg-ink-50 font-semibold">
              <td className="px-3 py-2 text-ink-900">합계</td>
              {items.map((item, i) => (
                <td
                  key={item.id}
                  style={hiCol === item.id ? undefined : heatStyle(totalsById[item.id], totalsMax)}
                  className={`px-3 py-2 text-right tabular-nums text-ink-900 ${hiClass(item.id)} ${isGroupStart(i) ? "border-l border-ink-200" : ""}`}
                >
                  {fmt.int(totalsById[item.id])}
                </td>
              ))}
              <td className="border-l border-ink-200 px-3 py-2 text-right tabular-nums text-ink-900">{fmt.int(grand)}</td>
            </tr>
          )}
          {displayRows.map((r) => {
            const rowTotal = rowTotals.get(r.bucket) || 0;
            return (
              <tr key={r.bucket} className="hover:bg-ink-50">
                <td className="whitespace-nowrap px-3 py-2 font-medium text-ink-900">{r.bucketLabel || r.bucket}</td>
                {items.map((item, i) => {
                  const v = itemValue(r, item, unit);
                  return (
                    <td
                      key={item.id}
                      style={hiCol === item.id ? undefined : heatStyle(v, maxVal)}
                      className={`px-3 py-2 text-right tabular-nums ${
                        hiCol === item.id ? "bg-brand-100/70 font-medium text-ink-900" : "text-ink-600"
                      } ${isGroupStart(i) ? "border-l border-ink-200" : ""}`}
                    >
                      {fmt.int(v)}
                    </td>
                  );
                })}
                <td
                  style={heatStyle(rowTotal, rowTotalMax)}
                  className="border-l border-ink-200 px-3 py-2 text-right tabular-nums font-semibold text-ink-900"
                >
                  {fmt.int(rowTotal)}
                  <RowDelta
                    cur={rowTotal}
                    prev={prevTotalByBucket.has(r.bucket) ? prevTotalByBucket.get(r.bucket) : null}
                    hidden={cumulative}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// 그룹 헤더 라벨 — 차트 라인과 같은 색 점을 붙여 "이 열 = 저 라인"이 한눈에 이어지게
// (2026-07-18, "다 쪼개면 헷갈린다 → 색으로 눈에 띄게" 피드백의 절충).
function GroupLabel({ superGroup, children }) {
  const color = TOOL_LINE_COLORS[superGroup];
  // "·" 조합 라벨(맞춤지시·기사진단 등)은 자동 줄바꿈에 맡기면 단어 중간이나 · 가 혼자
  // 남는 어색한 지점에서 꺾인다(2026-07-18 피드백 — "텍스트가 뒤죽박죽") — · 기준으로
  // 항상 딱 두 줄로 쌓아 예측 가능하게 렌더링한다. 짧은 라벨은 한 줄 고정(nowrap).
  const text = typeof children === "string" ? children : null;
  const parts = text && text.includes("·") ? text.split("·") : null;
  return (
    <span className="inline-flex items-center gap-1.5 text-left">
      <GroupIcon superGroup={superGroup} color={color} className="h-3.5 w-3.5" />
      {parts ? (
        <span className="leading-tight">
          {parts.map((p, i) => (
            <span key={p} className="block whitespace-nowrap">
              {p}{i < parts.length - 1 ? "·" : ""}
            </span>
          ))}
        </span>
      ) : (
        <span className="whitespace-nowrap">{children}</span>
      )}
    </span>
  );
}

// 최신순/과거순 방향 토글 — TrendTable·전체 사용자 탭과 같은 화살표 시각 언어로 통일(2026-07-16).
function DateTh({ dir, onToggle }) {
  const Icon = dir === "desc" ? ChevronDown : ChevronUp;
  return (
    <th rowSpan={2} className="w-24 px-3 py-2 text-left align-bottom text-xs font-semibold uppercase tracking-wide text-ink-500">
      <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 hover:text-ink-800">
        기간
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}
